import React from "react";
import clsx from "clsx";

import { getAirLevelAverage } from "../../../libs/iot";

import {
  AirTagInfo,
  FloorAirInfo,
  SpaceAirInfo,
  TAG_TYPE,
} from "../../../services/iotServices";

import CommonPopup from "..";
import HorizontalRule from "../../Rule/Horizontal";
import {
  getAirLevelBgColor,
  getAirLevelText,
  getAirLevelTextColor,
} from "../../../pages/Manager/Air";
import CommonButton from "../../Button";

type Props = {
  popup: string;
  spaceInfo?: SpaceAirInfo;
  onClose?: () => void;
  floorInfo?: FloorAirInfo;
};

export default function PopupSpaceAir({
  popup,
  spaceInfo,
  onClose,
  floorInfo,
}: Props) {
  const levelAverage = getAirLevelAverage(spaceInfo);
  const score = spaceInfo?.tag.find(
    (tag) => tag.tagTypeId === TAG_TYPE.AWAIR_SCORE
  );
  const temperature = spaceInfo?.tag.find(
    (tag) => tag.tagTypeId === TAG_TYPE.AWAIR_TEMPERATURE
  );
  const humidity = spaceInfo?.tag.find(
    (tag) => tag.tagTypeId === TAG_TYPE.AWAIR_HUMIDITY
  );
  const co2 = spaceInfo?.tag.find((tag) => tag.tagTypeId === TAG_TYPE.CO2);
  const chemical = spaceInfo?.tag.find(
    (tag) => tag.tagTypeId === TAG_TYPE.CHEMICAL
  );
  const dust = spaceInfo?.tag.find((tag) => tag.tagTypeId === TAG_TYPE.DUST);
  const light = spaceInfo?.tag.find((tag) => tag.tagTypeId === TAG_TYPE.LIGHT);
  const noise = spaceInfo?.tag.find((tag) => tag.tagTypeId === TAG_TYPE.NOISE);
  const levelScore = score?.value ?? levelAverage;

  return (
    <CommonPopup
      isOpen={popup === "detailFloorAir"}
      title="공기질 현황"
      unUseHorizonRule
      onClose={onClose}
      padding={0}
    >
      <div className="p-5 pt-0 flex flex-col gap-2.5 items-center pb-4">
        <PopupSpaceAirIcon />

        <div className="pb-[15px] flex flex-col gap-[5px] items-center">
          <div className="text-3xl font-semibold">{floorInfo?.floorName}</div>
          <div className="text-sm text-brand-neutral-50">
            ( 측정장소 : {floorInfo?.floorName} {spaceInfo?.spaceName})
          </div>
        </div>

        <HorizontalRule margin="0" />

        <div className="py-[15px] flex flex-col items-center">
          <div
            className={clsx([
              "text-5xl font-semibold",
              getAirLevelTextColor(levelScore),
            ])}
          >
            {levelScore.toFixed(0)}
          </div>
          <div
            className={clsx([
              "text-white rounded-full px-2.5 py-[5px] text-sm font-semibold",
              getAirLevelBgColor(levelScore),
            ])}
          >
            {getAirLevelText(levelScore)}
          </div>
        </div>

        <div className="flex flex-col gap-5 w-full">
          <div className="grid grid-cols-5">
            <PopupSpaceAirTagGraph
              tag={temperature}
              type="온도"
              id="temperature"
            />
            <PopupSpaceAirTagGraph tag={humidity} type="습도" id="humidity" />
            <PopupSpaceAirTagGraph tag={co2} type="이산화탄소" id="co2" />
            <PopupSpaceAirTagGraph
              tag={chemical}
              type="화학물질"
              id="chemical"
            />
            <PopupSpaceAirTagGraph tag={dust} type="초미세먼지" id="dust" />
          </div>
          <HorizontalRule margin="0" />
          <div className="flex gap-2.5">
            <div className="flex items-center gap-2.5 flex-1 justify-center">
              <span className="text-sm text-brand-neutral-60">조도</span>
              <div>
                <span className="text-base font-semibold">{light?.value}</span>
                <span className="text-sm font-bold">{light?.tagTypeUnit}</span>
              </div>
            </div>
            <div className="flex items-center gap-2.5 flex-1 justify-center">
              <span className="text-sm text-brand-neutral-60">소음</span>
              <div>
                <span className="text-base font-semibold">{noise?.value}</span>
                <span className="text-sm font-bold">{noise?.tagTypeUnit}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <CommonButton
        className="w-screen h-bottom-button rounded-none"
        onClick={() => onClose?.()}
      >
        확인
      </CommonButton>
    </CommonPopup>
  );
}

export const PopupSpaceAirTagGraph = ({
  tag,
  type,
  id,
}: {
  tag?: AirTagInfo;
  type: string;
  id: string;
}) => {
  const getLevel = (tag?: AirTagInfo) => {
    return tag?.level ? Number(tag?.level?.replace("level0", "")) : 5;
  };

  return (
    <div className="flex flex-col gap-2.5 items-center">
      {tag?.value === undefined ? <PopupSpaceAirGraphEmpty /> : <PopupSpaceAirGraph level={getLevel(tag)} id={id} />} 
      <div className="flex flex-col gap-0.5 items-center">
        <div className="text-sm font-semibold">
          { (tag?.value === undefined) ? 
            <span className="text-xs font-semibold">
            정보없음
          </span>
           : <span className="text-xs font-semibold">
          {tag?.value}
          {tag?.tagTypeUnit}
        </span>} 
        </div>
        <div className="text-xs text-brand-neutral-60">{type}</div>
      </div>
    </div>
  );
};

export type PopupSpaceAirGraphProps = { level: number; id?: string };

export const PopupSpaceAirGraph = ({ level, id }: PopupSpaceAirGraphProps) => {
  const defaultTop = 20;
  const totalSize = 200;
  const levelSize = 40;

  const getHeight = (level: number) => {
    return totalSize - levelSize * Math.abs(level - 5);
  };

  const getY = (level: number) => {
    return levelSize * Math.abs(level - 5) + defaultTop;
  };

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="30"
      height="200"
      viewBox="0 0 30 200"
      fill="none"
    >
      <rect x="10" width="10" height="200" rx="5" fill="#DDDDDD" />
      <mask
        id={`mask${id}`}
        style={{ maskType: "alpha" }}
        maskUnits="userSpaceOnUse"
        x="10"
        y={getY(level)}
        width="10"
        height={getHeight(level)}
      >
        <rect
          x="10"
          y={getY(level)}
          width="10"
          height={getHeight(level) - defaultTop}
          rx="5"
          fill={`url(#paint0${id})`}
        />
      </mask>
      <g mask={`url(#mask${id})`}>
        <rect
          x="10"
          // y={defaultTop + levelSize * (level - 1)}
          y={getY(level)}
          width="10"
          height={getHeight(level) - defaultTop}
          rx="5"
          fill={`url(#paint1${id})`}
        />
      </g>
      {/* 닷 보더 */}
      <circle cx="15" cy={getY(level)} r="10" fill={`url(#paint1${id})`} />
      {/* 닷 */}
      <circle cx="15" cy={getY(level)} r="3" fill="#F5F5F5" />
      <defs>
        <linearGradient
          id={`paint0${id}`}
          x1="15"
          y1="0"
          x2="15"
          y2="200"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#E0004D" />
          <stop offset="0.265625" stopColor="#FF5C00" />
          <stop offset="0.510417" stopColor="#F2AE00" />
          <stop offset="0.760417" stopColor="#18CC3F" />
          <stop offset="1" stopColor="#2050CD" />
        </linearGradient>
        <linearGradient
          id={`paint1${id}`}
          x1="15"
          y1="0"
          x2="15"
          y2="200"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#E0004D" />
          <stop offset="0.265625" stopColor="#FF5C00" />
          <stop offset="0.510417" stopColor="#F2AE00" />
          <stop offset="0.760417" stopColor="#18CC3F" />
          <stop offset="1" stopColor="#2050CD" />
        </linearGradient>
      </defs>
    </svg>
  );
};


export const PopupSpaceAirGraphEmpty = () => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="30"
      height="200"
      viewBox="0 0 30 200"
      fill="none"
    >
      <rect x="10" width="10" height="200" rx="5" fill="#DDDDDD" />
    </svg>
  );
};

export const PopupSpaceAirIcon = () => (
  <svg
    width="36"
    height="36"
    viewBox="0 0 36 36"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M21.0466 21.1526H20.2594C17.827 21.1526 16.5518 16.8389 16.5518 12.5803C16.5518 8.32161 17.8191 4 20.2515 4H21.0387V21.1526H21.0466Z"
      stroke="black"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M5.58669 29.9218C4.01233 29.9218 3.32748 29.3 3.02836 28.7804L2.63477 28.0956L3.31961 27.702L16.804 19.9168L17.481 19.5232L17.8746 20.2002C18.276 20.8929 18.4256 22.1209 16.8591 24.0259C15.8043 25.309 14.1827 26.6157 12.3013 27.702C9.89256 29.0953 7.38146 29.9218 5.57882 29.9218H5.58669Z"
      stroke="black"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M32.7125 31.9921L32.0277 31.5986L18.5354 23.8134L17.8506 23.4198L18.2442 22.7349C18.5433 22.2154 19.236 21.5935 20.8025 21.5935C22.6051 21.5935 25.1163 22.42 27.525 23.8134C31.2169 25.9387 34.3105 29.1977 33.0983 31.3152L32.7047 32L32.7125 31.9921Z"
      stroke="black"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M21.3858 21.3102C21.3858 23.0971 19.9374 24.5534 18.1426 24.5534C16.3478 24.5534 14.8994 23.105 14.8994 21.3102C14.8994 19.5154 16.3478 18.067 18.1426 18.067C19.9374 18.067 21.3858 19.5154 21.3858 21.3102Z"
      fill="white"
      stroke="black"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M4.44238 13.5249H10.8894"
      stroke="url(#paint0_linear_930_19356)"
      strokeWidth="2"
      strokeLinecap="square"
      strokeLinejoin="round"
    />
    <path
      d="M30.5292 13.2965C31.8044 13.2965 32.8356 14.3277 32.8356 15.6029C32.8356 16.8782 31.8044 17.9094 30.5292 17.9094H25.2314"
      stroke="url(#paint1_linear_930_19356)"
      strokeWidth="2"
      strokeLinecap="square"
      strokeLinejoin="round"
    />
    <defs>
      <linearGradient
        id="paint0_linear_930_19356"
        x1="10.8894"
        y1="13.5249"
        x2="8.93946"
        y2="16.7604"
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor="#E100A3" />
        <stop offset="1" stopColor="#EA27C2" />
      </linearGradient>
      <linearGradient
        id="paint1_linear_930_19356"
        x1="32.8356"
        y1="13.2965"
        x2="25.5203"
        y2="16.4002"
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor="#E100A3" />
        <stop offset="1" stopColor="#EA27C2" />
      </linearGradient>
    </defs>
  </svg>
);
